import React from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import SimpleTableContent from 'src/components/simple-table-content';
import { noop } from 'lodash';

const INACTIVATION_SORT_MAP = {
  operationId: 'operationId',
  login: 'login',
  role: 'role',
  positionMap: 'positionMap',
  technology: 'technology',
  gu: 'gu',
  profileStatus: 'profileStatus',
  pdm: 'pdm',
  deactivatedDate: 'deactivatedDate',
  deactivatedPeriod: 'deactivatedPeriod',
  deactivatedEnd: 'deactivatedEnd',
};

/**
 * Inactive Profiles table component.
 * @param {Object} props
 * @param {Array} props.data
 * @param {Object} props.pagination
 * @param {boolean} props.loading
 * @param {Function} props.onPaginationChange
 * @param {Function} props.onSortChange
 * @returns {JSX.Element}
 */
function InactivationTable({ data, pagination, loading, onPaginationChange, onSortChange }) {
  const { t } = useTranslation();

  const columns = [
    {
      title: t('reports.inactivationOperationId'),
      dataIndex: 'operationId',
      width: 80,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text ? text.split('-')[0] : '-'} />,
    },
    {
      title: 'Login',
      dataIndex: 'login',
      width: 130,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('role'),
      dataIndex: 'role',
      width: 150,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('positionMap'),
      dataIndex: 'positionMap',
      width: 130,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('tech'),
      dataIndex: 'technology',
      width: 150,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: 'GU',
      dataIndex: 'gu',
      width: 120,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('statusProfile'),
      dataIndex: 'profileStatus',
      width: 110,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text === 'Active' ? t('active') : text === 'Inactive' ? t('inactive') : text} />,
    },
    {
      title: t('reports.deactivatedBy'),
      dataIndex: 'pdm',
      width: 130,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.deactivatedDate'),
      dataIndex: 'deactivatedDate',
      width: 130,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.deactivatedPeriod'),
      dataIndex: 'deactivatedPeriod',
      width: 130,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text ? text.replace('days', t('days')) : text} />,
    },
    {
      title: t('reports.inactivationReason'),
      dataIndex: 'inactivationReason',
      width: 200,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.deactivatedEnd'),
      dataIndex: 'deactivatedEnd',
      width: 130,
      sorter: true,
      align: 'left',
      onCell: () => ({ style: { verticalAlign: 'top', textAlign: 'left' } }),
      onHeaderCell: () => ({ style: { textAlign: 'left' } }),
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
  ];

  const paginationConfig = {
    current: pagination.currentPage,
    total: pagination.totalCount,
    pageSize: pagination.limit,
    onChange: onPaginationChange,
    showTotal: (total) => `${total} ${t('reports.recordsFound')}`,
  };

  const handleTableChange = (_, __, sorter) => {
    const sort = INACTIVATION_SORT_MAP[sorter.field];
    const order = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
    onSortChange(sort && order ? { sort, order } : {});
  };

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey='operationId'
      loading={loading}
      pagination={paginationConfig}
      scroll={{ x: 1500 }}
      onChange={handleTableChange}
    />
  );
}

InactivationTable.defaultProps = {
  data: [],
  pagination: { currentPage: 1, totalCount: 0, limit: 10 },
  loading: false,
  onPaginationChange: noop,
  onSortChange: noop,
};

export default InactivationTable;
